import React, { useLayoutEffect, useMemo, useRef, useState, useCallback } from 'react';
import { useWindowVirtualizer } from '@tanstack/react-virtual';

const GAP_PX = 15;
const CARD_WIDTH_PX = 230;

type Cell<T> = { kind: 'lead'; node: React.ReactNode } | { kind: 'item'; item: T };

function buildCells<T>(items: T[], lead?: React.ReactNode): Cell<T>[] {
  const cells: Cell<T>[] = [];
  if (lead != null) {
    cells.push({ kind: 'lead', node: lead });
  }
  items.forEach((item) => cells.push({ kind: 'item', item }));
  return cells;
}

function chunkCells<T>(cells: Cell<T>[], columns: number): Cell<T>[][] {
  const rows: Cell<T>[][] = [];
  for (let i = 0; i < cells.length; i += columns) {
    rows.push(cells.slice(i, i + columns));
  }
  return rows;
}

export type VirtualizedProductCardGridProps<T extends { _id?: string }> = {
  items: T[];
  /** When true, two columns and shorter row height (matches ProductCard mobile). */
  isMobileLayout: boolean;
  /** Optional first grid cell(s), e.g. “create product” card (dashboard). */
  leadingSlot?: React.ReactNode;
  renderItem: (item: T) => React.ReactNode;
  /** Extra overscan rows above/below viewport */
  overscan?: number;
};

/**
 * Row-major virtualized grid using window scroll (no inner scroll container).
 * Only visible rows stay mounted → fewer images / less DOM.
 */
export function VirtualizedProductCardGrid<T extends { _id?: string }>({
  items,
  isMobileLayout,
  leadingSlot,
  renderItem,
  overscan = 3,
}: VirtualizedProductCardGridProps<T>) {
  const listRef = useRef<HTMLDivElement>(null);
  const [width, setWidth] = useState(0);
  const [scrollMargin, setScrollMargin] = useState(0);

  const measureScrollMargin = useCallback(() => {
    const el = listRef.current;
    if (!el) return;
    const next = el.getBoundingClientRect().top + window.scrollY;
    setScrollMargin((prev) => (Math.abs(prev - next) < 0.5 ? prev : next));
  }, []);

  useLayoutEffect(() => {
    const el = listRef.current;
    if (!el) return;
    const ro = new ResizeObserver(() => {
      setWidth(el.clientWidth);
      measureScrollMargin();
    });
    ro.observe(el);
    setWidth(el.clientWidth);
    measureScrollMargin();
    return () => ro.disconnect();
  }, [measureScrollMargin]);

  useLayoutEffect(() => {
    measureScrollMargin();
    const onScroll = () => measureScrollMargin();
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', measureScrollMargin);
    return () => {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', measureScrollMargin);
    };
  }, [measureScrollMargin, items.length, leadingSlot, isMobileLayout]);

  const columns = useMemo(() => {
    if (isMobileLayout) return 2;
    if (width <= 0) return Math.max(1, Math.floor((800 + GAP_PX) / (CARD_WIDTH_PX + GAP_PX)));
    return Math.max(1, Math.floor((width + GAP_PX) / (CARD_WIDTH_PX + GAP_PX)));
  }, [width, isMobileLayout]);

  const rowHeight = isMobileLayout ? 255 : 295;

  const rows = useMemo(() => {
    const cells = buildCells(items, leadingSlot);
    return chunkCells(cells, columns);
  }, [items, leadingSlot, columns]);

  const virtualizer = useWindowVirtualizer({
    count: rows.length,
    estimateSize: () => rowHeight,
    overscan,
    scrollMargin,
  });

  return (
    <div ref={listRef} style={{ width: '100%', position: 'relative' }}>
      <div
        style={{
          height: virtualizer.getTotalSize(),
          position: 'relative',
          width: '100%',
        }}
      >
        {virtualizer.getVirtualItems().map((vRow) => {
          const row = rows[vRow.index];
          if (!row) return null;
          return (
            <div
              key={vRow.key}
              data-index={vRow.index}
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: vRow.size,
                // Measurements use document-aligned starts (paddingStart + scrollMargin);
                // rows are positioned inside listRef, so subtract scrollMargin to avoid a gap.
                transform: `translateY(${vRow.start - scrollMargin}px)`,
              }}
            >
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'row',
                  flexWrap: 'nowrap',
                  gap: GAP_PX,
                  justifyContent: 'flex-start',
                  alignItems: 'stretch',
                }}
              >
                {row.map((cell, colIdx) => (
                  <div
                    key={
                      cell.kind === 'lead'
                        ? 'lead-slot'
                        : (cell.item._id as string) ?? `item-${vRow.index}-${colIdx}`
                    }
                    style={{
                      width: isMobileLayout ? 'calc(50% - 8px)' : CARD_WIDTH_PX,
                      flexShrink: 0,
                      marginBottom: GAP_PX,
                    }}
                  >
                    {cell.kind === 'lead' ? cell.node : renderItem(cell.item)}
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
